<template>
    <vx-expandable-card :class="{
        'opacity-40 cursor-not-allowed': props.disabled
    }">
        <template v-slot:header>
            <div class="flex flex-col items-start" :class="{
                'opacity-40 cursor-not-allowed': props.disabled
            }">
                <slot name="header"></slot>

                <span class="text-sm font-normal text-slate-500">
                    <slot name="subheader"></slot>
                </span>
            </div>
        </template>
        <template v-slot:content>
            <div class="flex flex-col gap-6">
                <div class="relative">
                    <vx-selectable
                        :placeholder="props.placeholder"
                        size="lg"
                        :taggable="true"
                        @search="emit('search', $event)"
                        :options="props.options || []"
                        v-model.lowercase="inputModel"
                        :disabled="props.disabled || props.loading || hitMax"
                        :error="displayFirstError(validation.$errors)"
                        @option:selected="addValue()"
                    ></vx-selectable>
                    <div v-if="$slots['max-values']" class="mt-2 px-0.5 text-xs text-slate-600">
                        <slot name="max-values"></slot>
                    </div>
                </div>
            </div>
            <div class="flex flex-col mt-6">
                <div v-if="props.loading || !props.modelValue.length" class="text-center py-10">
                    <vx-icon-loading v-if="props.loading" class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
                    <div v-else class="text-xl text-slate-500">
                        <slot name="empty"></slot>
                    </div>
                </div>
                <transition-group tag="div" class="relative"
                    enterActiveClass="transition-all transition-ease-out duration-300 origin-right"
                    enterFromClass="opacity-0 -translate-y-20 scale-95"
                    enterToClass="opacity-100 translate-y-0"
                    leaveActiveClass="transition-all transition-ease-out absolute duration-150"
                    leaveFromClass="opacity-100 translate-y-0" leaveToClass="opacity-0 scale-90"
                    moveClass="transition-all transition-ease-out duration-300">
                    <div v-for="(item, index) in props.modelValue" :key="`value-${item.id}`"
                        class="flex w-full flex-row-reverse items-center justify-between border-0" :class="{
                            'border-b border-solid border-b-slate-200': index !== item.length - 1
                        }" data-test="value">
                        <button type="button" :disabled="props.disabled" @click="removeValue(item.id)"
                            class="group/del peer relative px-4 py-2 transition focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-500" :class="{
                                'cursor-pointer': !props.disabled,
                                'pointer-events-none cursor-not-allowed opacity-40': props.disabled,
                            }" data-test="remove-value" aria-label="Remove value">
                            <font-awesome-icon widthAuto :icon="faCircleMinusReg"
                                class="text-slate-500 transition group-hover/del:opacity-0 group-focus-visible/del:opacity-0" />
                            <font-awesome-icon widthAuto :icon="faCircleMinus"
                                class="absolute inset-0 m-auto text-rose-500 opacity-0 transition group-hover/del:opacity-100 group-focus-visible/del:opacity-100" />
                        </button>

                        <span
                            class="truncate content-[''] relative transition after:absolute after:-inset-x-1 after:top-1/2 after:h-px after:origin-left after:-translate-y-1/2 after:scale-x-0 after:bg-black after:transition peer-hover:opacity-50 peer-hover:after:scale-x-100 peer-focus-visible:opacity-50 peer-focus-visible:after:scale-x-100">
                            {{ item.value }}
                        </span>
                    </div>
                </transition-group>
            </div>
            <slot name="footer"></slot>
        </template>
    </vx-expandable-card>
</template>
<script setup>
import { VxExpandableCard, VxIconLoading, VxSelectable } from '@voxie/frontend-components';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faCircleMinus } from '@fortawesome/pro-solid-svg-icons';
import { faCircleMinus as faCircleMinusReg } from '@fortawesome/pro-regular-svg-icons';
import { ref, computed, onMounted } from 'vue';
import useVuelidate from '@vuelidate/core';
import { displayFirstError } from '~/utils/validation';
import { randomString } from '~/utils/string';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => ([
            {
                id: randomString(),
                value: '',
            }
        ])
    },
    placeholder: {
        type: String,
        default: '',
    },
    options: {
        type: Array,
        default: () => ([]),
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    maxValues: {
        type: [Number, undefined],
    },
    validationRules: {
        type: Object,
        default: () => ({}),
    }
});

const emit = defineEmits(['update:modelValue', 'search']);

const inputModel = ref('');

onMounted(() => {
    emit('search', inputModel.value)
});

const hitMax = computed(() => undefined !== props.maxValues && props.modelValue.length >= props.maxValues);
const addValue = async () => {
    if (
        hitMax.value || props.disabled || props.loading
    ) {
        return;
    }

    const isValid = await validation.value.$validate();
    if (!isValid) {
        return;
    }

    const collection = props.modelValue;

    collection.unshift({
        id: randomString(20),
        value: inputModel.value,
    });

    emit('update:modelValue', collection);

    inputModel.value = '';
    validation.value.$reset();
}

const removeValue = (id) => {
    if (props.disabled || props.loading) {
        return;
    }

    emit('update:modelValue', props.modelValue.filter((item) => item.id !== id));
}

const rules = computed(() => props.validationRules);
const validation = useVuelidate(rules, inputModel);
</script>
